import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import axios from "../../../api/axios";

const DEFAULT_CONTACT_TYPE = "Kontakt";
const CONTACT_TYPE_OPTIONS = [
  "Kontakt",
  "Kundes Prosjektleder",
  "Kundens byggeleder",
  "Byggherre",
  "Fakturakontakt",
  "SHA/HMS",
  "Innkjop",
  "Annet",
];

const tabStyles = (active) =>
  active
    ? "border-cyan-500 bg-cyan-50 text-cyan-700"
    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700";

const toProjectContactPayload = (entry) => {
  const payload = {
    contact_type: entry?.contact_type || DEFAULT_CONTACT_TYPE,
    is_primary: !!entry?.is_primary,
  };

  if (entry?.id) payload.id = entry.id;
  if (entry?.delete) payload.delete = true;

  if (entry?.user?.id) {
    payload.user_id = entry.user.id;
  } else if (entry?.contact?.id) {
    payload.contact_id = entry.contact.id;
  } else if (entry?.customer_contact?.id) {
    payload.customer_contact_id = entry.customer_contact.id;
  } else if (entry?.user_id) {
    payload.user_id = entry.user_id;
  } else if (entry?.contact_id) {
    payload.contact_id = entry.contact_id;
  } else if (entry?.customer_contact_id) {
    payload.customer_contact_id = entry.customer_contact_id;
  }

  return payload;
};

const buildSelectionKey = (sourceType, id) => `${sourceType}-${id}`;

const buildContactTypeOptions = (currentValue) => {
  const normalizedValue = (currentValue || DEFAULT_CONTACT_TYPE).trim();

  if (!normalizedValue || CONTACT_TYPE_OPTIONS.includes(normalizedValue)) {
    return CONTACT_TYPE_OPTIONS;
  }

  return [normalizedValue, ...CONTACT_TYPE_OPTIONS];
};

const personMatchesSearch = (person, searchQuery) => {
  if (!searchQuery.trim()) return true;

  const needle = searchQuery.toLowerCase();
  return [
    person?.displayTitle,
    person?.name,
    person?.email,
    person?.mobile,
    person?.mobile_work,
  ]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(needle));
};

const getContactMeta = (entry) => {
  const isEmployee = entry?.source_type === "employee" || entry?.user?.id;
  const person = isEmployee
    ? entry?.user
    : entry?.contact || entry?.customer_contact;

  return {
    isEmployee,
    person,
    email: person?.email,
    mobile: isEmployee ? person?.mobile_work || person?.mobile : person?.mobile,
    link: isEmployee
      ? person?.id
        ? `/dashboard/users/${person.id}`
        : null
      : entry?.contact?.id
      ? `/dashboard/contacts/${entry.contact.id}`
      : null,
  };
};

const sortContactsForProject = (contacts, projectCustomerId) => {
  return [...contacts].sort((a, b) => {
    const aMatchesProject = a?.customer?.id === projectCustomerId ? 0 : 1;
    const bMatchesProject = b?.customer?.id === projectCustomerId ? 0 : 1;

    if (aMatchesProject !== bMatchesProject) {
      return aMatchesProject - bMatchesProject;
    }

    const aName = (a?.displayTitle || a?.name || "").toLowerCase();
    const bName = (b?.displayTitle || b?.name || "").toLowerCase();
    return aName.localeCompare(bName);
  });
};

export const ProjectContactsSection = ({
  project,
  onRefresh,
  canManage = false,
  className = "",
}) => {
  const [projectContacts, setProjectContacts] = useState(
    project?.project_contacts || []
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState({
    contact_type: DEFAULT_CONTACT_TYPE,
    is_primary: false,
  });
  const [activeTab, setActiveTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectionState, setSelectionState] = useState({});

  useEffect(() => {
    const nextContacts = project?.project_contacts || [];
    setProjectContacts(nextContacts);
  }, [project?.project_contacts]);

  useEffect(() => {
    if (!dialogOpen) return;

    let cancelled = false;
    const trimmedQuery = searchQuery.trim();

    const loadOptions = async () => {
      setLoadingOptions(true);

      try {
        const [employeesResponse, contactsResponse] = await Promise.all([
          axios.get("/users", {
            params: {
              page: 1,
              per_page: 200,
              sortBy: "name",
              sort: "ASC",
              query: trimmedQuery || undefined,
              fields: "id,displayTitle,name,email,mobile,mobile_work,image_url",
              filters: [{ name: "employee", value: true }],
            },
          }),
          axios.get("/contacts", {
            params: {
              page: 1,
              per_page: 200,
              sortBy: "name",
              sort: "ASC",
              query: trimmedQuery || undefined,
              fields: "id,displayTitle,name,email,mobile,customer",
            },
          }),
        ]);

        if (cancelled) return;

        setEmployees(employeesResponse?.data?.data || []);
        setCustomerContacts(
          sortContactsForProject(
            contactsResponse?.data?.data || [],
            project?.customer?.id
          )
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading project contact options:", error);
        toast.error("Kunne ikke laste prosjektkontakter");
      } finally {
        if (!cancelled) {
          setLoadingOptions(false);
        }
      }
    };

    const timeoutId = setTimeout(loadOptions, trimmedQuery ? 300 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [dialogOpen, project?.customer?.id, searchQuery]);

  const existingEmployeeIds = useMemo(
    () =>
      new Set(projectContacts.map((entry) => entry?.user?.id).filter(Boolean)),
    [projectContacts]
  );

  const existingContactIds = useMemo(
    () =>
      new Set(
        projectContacts.map((entry) => entry?.contact?.id).filter(Boolean)
      ),
    [projectContacts]
  );

  const filteredEmployees = useMemo(
    () => employees.filter((person) => personMatchesSearch(person, searchQuery)),
    [employees, searchQuery]
  );

  const filteredCustomerContacts = useMemo(
    () =>
      customerContacts.filter((person) =>
        personMatchesSearch(person, searchQuery)
      ),
    [customerContacts, searchQuery]
  );

  const selectedCount = useMemo(
    () =>
      Object.values(selectionState).filter((entry) => entry.checked).length,
    [selectionState]
  );
  const emailSubject = useMemo(
    () => encodeURIComponent(project?.displayTitle || project?.name || "Prosjekt"),
    [project?.displayTitle, project?.name]
  );

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setSelectionState({});
    setSearchQuery("");
  };

  const closeEditDialog = () => {
    if (saving) return;
    setEditingContact(null);
    setEditForm({
      contact_type: DEFAULT_CONTACT_TYPE,
      is_primary: false,
    });
  };

  const persistProjectContacts = async (entries) => {
    if (!project?.id) return null;

    setSaving(true);

    try {
      const response = await axios.put(`/projects/${project.id}`, {
        project: {
          id: project.id,
          project_contacts: entries.map(toProjectContactPayload),
        },
      });

      const updatedProject = response?.data || {};
      const updatedContacts = updatedProject?.project_contacts || [];

      setProjectContacts(updatedContacts);

      if (onRefresh) {
        onRefresh(updatedProject);
      }

      return updatedProject;
    } catch (error) {
      console.error("Error updating project contacts:", error);
      const message =
        error?.response?.data?.errors?.join(", ") ||
        "Kunne ikke oppdatere prosjektkontakter";
      toast.error(message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (entry) => {
    const nextEntries = projectContacts.map((currentEntry) =>
      currentEntry.id === entry.id
        ? { ...currentEntry, delete: true }
        : currentEntry
    );

    const updatedProject = await persistProjectContacts(nextEntries);
    if (updatedProject) {
      toast.success("Kontakt fjernet fra prosjektet");
    }
  };

  const openEditDialog = (entry) => {
    setEditingContact(entry);
    setEditForm({
      contact_type: entry.contact_type || DEFAULT_CONTACT_TYPE,
      is_primary: !!entry.is_primary,
    });
  };

  const handleEditSave = async () => {
    if (!editingContact?.id) return;

    const nextEntries = projectContacts.map((currentEntry) => ({
      ...currentEntry,
      contact_type:
        currentEntry.id === editingContact.id
          ? editForm.contact_type || DEFAULT_CONTACT_TYPE
          : currentEntry.contact_type || DEFAULT_CONTACT_TYPE,
      is_primary:
        currentEntry.id === editingContact.id
          ? !!editForm.is_primary
          : editForm.is_primary
          ? false
          : !!currentEntry.is_primary,
    }));

    const updatedProject = await persistProjectContacts(nextEntries);
    if (updatedProject) {
      closeEditDialog();
      toast.success("Prosjektkontakt oppdatert");
    }
  };

  const handleSelectionToggle = (sourceType, person) => {
    const key = buildSelectionKey(sourceType, person.id);
    setSelectionState((prev) => {
      const current = prev[key] || {};
      return {
        ...prev,
        [key]: {
          checked: !current.checked,
          contact_type: current.contact_type || DEFAULT_CONTACT_TYPE,
          is_primary: !!current.is_primary,
          sourceType,
          personId: person.id,
        },
      };
    });
  };

  const handleSelectionTypeChange = (sourceType, personId, value) => {
    const key = buildSelectionKey(sourceType, personId);
    setSelectionState((prev) => ({
      ...prev,
      [key]: {
        checked: prev[key]?.checked ?? true,
        contact_type: value,
        is_primary: !!prev[key]?.is_primary,
        sourceType,
        personId,
      },
    }));
  };

  const handleSelectionPrimaryChange = (sourceType, personId, checked) => {
    const key = buildSelectionKey(sourceType, personId);

    setSelectionState((prev) => {
      const nextState = { ...prev };

      if (checked) {
        Object.keys(nextState).forEach((entryKey) => {
          nextState[entryKey] = {
            ...nextState[entryKey],
            is_primary: false,
          };
        });
      }

      nextState[key] = {
        checked: nextState[key]?.checked ?? true,
        contact_type: nextState[key]?.contact_type || DEFAULT_CONTACT_TYPE,
        is_primary: checked,
        sourceType,
        personId,
      };

      return nextState;
    });
  };

  const handleAddSelected = async () => {
    const additions = Object.values(selectionState)
      .filter((entry) => entry.checked)
      .map((entry) => ({
        contact_type: entry.contact_type?.trim() || DEFAULT_CONTACT_TYPE,
        is_primary: !!entry.is_primary,
        ...(entry.sourceType === "employee"
          ? { user_id: entry.personId }
          : { contact_id: entry.personId }),
      }));

    if (additions.length === 0) {
      toast.error("Velg minst en kontakt");
      return;
    }

    const updatedProject = await persistProjectContacts([
      ...projectContacts,
      ...additions,
    ]);

    if (updatedProject) {
      closeDialog();
      toast.success("Prosjektkontakter lagt til");
    }
  };

  const renderSelectableRow = (person, sourceType) => {
    const key = buildSelectionKey(sourceType, person.id);
    const selected = selectionState[key] || {};
    const alreadyAdded =
      sourceType === "employee"
        ? existingEmployeeIds.has(person.id)
        : existingContactIds.has(person.id);
    const phone = person.mobile_work || person.mobile;

    return (
      <label
        key={key}
        className={`flex items-start justify-between rounded-xl border px-4 py-3 transition ${
          alreadyAdded
            ? "border-gray-200 bg-gray-50 opacity-70"
            : selected.checked
            ? "border-cyan-400 bg-cyan-50"
            : "border-gray-200 bg-white hover:border-gray-300"
        }`}
      >
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
            checked={alreadyAdded ? true : !!selected.checked}
            disabled={alreadyAdded}
            onChange={() => handleSelectionToggle(sourceType, person)}
          />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-gray-900">
                {person.displayTitle || person.name}
              </p>
              <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                {sourceType === "employee" ? "Ansatt" : "Kundekontakt"}
              </span>
              {alreadyAdded && (
                <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700">
                  Allerede lagt til
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {person.email || "-"}
              {phone ? ` / ${phone}` : ""}
            </p>
            {sourceType === "customer_contact" && (
              <p className="mt-1 text-xs text-gray-400">
                {person?.customer?.displayTitle || "Ingen kunde koblet"}
              </p>
            )}
          </div>
        </div>

        <div className="ml-4 w-full max-w-[180px]">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
            Type
          </label>
          <select
            disabled={alreadyAdded || !selected.checked}
            value={selected.contact_type || DEFAULT_CONTACT_TYPE}
            onChange={(event) =>
              handleSelectionTypeChange(sourceType, person.id, event.target.value)
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:cursor-not-allowed disabled:bg-gray-100"
          >
            {buildContactTypeOptions(selected.contact_type).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              disabled={alreadyAdded || !selected.checked}
              checked={!!selected.is_primary}
              onChange={(event) =>
                handleSelectionPrimaryChange(
                  sourceType,
                  person.id,
                  event.target.checked
                )
              }
              className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 disabled:cursor-not-allowed"
            />
            Hovedkontakt
          </label>
        </div>
      </label>
    );
  };

  const renderDialogList = () => {
    if (loadingOptions) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
          Laster kontakter...
        </div>
      );
    }

    const items =
      activeTab === "employees" ? filteredEmployees : filteredCustomerContacts;

    if (items.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
          Ingen treff i denne fanen.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {items.map((person) =>
          renderSelectableRow(
            person,
            activeTab === "employees" ? "employee" : "customer_contact"
          )
        )}
      </div>
    );
  };

  return (
    <>
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-sky-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100">
                  <svg
                    className="h-6 w-6 text-cyan-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Prosjektkontakter
                  </h3>
                  <span className="inline-flex rounded-full bg-cyan-100 px-2 py-1 text-xs font-semibold text-cyan-800">
                    {projectContacts.length}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Ansatte og kundekontakter knyttet til prosjektet
                </p>
              </div>
            </div>

            {canManage && (
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="inline-flex items-center rounded-md border border-transparent bg-cyan-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
              >
                Legg til
              </button>
            )}
          </div>
        </div>

        <div className="bg-white px-6 py-6">
          {projectContacts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
              <p className="text-sm text-gray-500">
                Ingen ekstra prosjektkontakter er lagt til enna.
              </p>
              {canManage && (
                <button
                  type="button"
                  onClick={() => setDialogOpen(true)}
                  className="mt-4 inline-flex items-center rounded-md border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-100"
                >
                  Legg til forste kontakt
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {projectContacts.map((entry) => {
                const meta = getContactMeta(entry);

                return (
                  <div
                    key={entry.id}
                    className="rounded-xl border border-gray-200 p-4 transition hover:border-gray-300 hover:bg-gray-50"
                  >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Kontakt
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">
                            {meta.person?.displayTitle ||
                              meta.person?.name ||
                              entry.displayTitle ||
                              "-"}
                          </p>
                          {entry.is_primary && (
                            <span className="inline-flex rounded-full bg-cyan-100 px-2 py-1 text-xs font-semibold text-cyan-800">
                              Hovedkontakt
                            </span>
                          )}
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                              meta.isEmployee
                                ? "bg-orange-100 text-orange-700"
                                : "bg-emerald-100 text-emerald-700"
                            }`}
                          >
                            {meta.isEmployee ? "Ansatt" : "Kundekontakt"}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          {meta.email || "-"}
                          {meta.mobile ? ` / ${meta.mobile}` : ""}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Type
                        </label>
                        <div className="mt-1 space-y-1">
                          <p className="text-sm text-gray-900">
                            {entry.contact_type || DEFAULT_CONTACT_TYPE}
                          </p>
                          {entry.is_primary && (
                            <p className="text-xs font-medium uppercase tracking-wide text-cyan-700">
                              Hovedkontakt
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Handlinger
                        </p>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {meta.mobile && (
                            <a
                              href={`tel:${meta.mobile}`}
                              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              Ring
                            </a>
                          )}
                          {meta.email && (
                            <a
                              href={`mailto:${meta.email}?subject=${emailSubject}`}
                              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              E-post
                            </a>
                          )}
                          {meta.link && (
                            <Link
                              to={meta.link}
                              className="inline-flex items-center rounded-md border border-transparent bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700"
                            >
                              Apne
                            </Link>
                          )}
                          {canManage && (
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => openEditDialog(entry)}
                              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                              title="Rediger prosjektkontakt"
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.232 5.232l3.536 3.536M9 13l6.232-6.232a2.5 2.5 0 113.536 3.536L12.536 16.536A4 4 0 019.707 17.707L6 18l.293-3.707A4 4 0 017.464 11.464L13.696 5.232"
                                />
                              </svg>
                            </button>
                          )}
                          {canManage && (
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => handleRemove(entry)}
                              className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Fjern
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <PMDialog
        open={dialogOpen}
        onClose={closeDialog}
        title="Legg til prosjektkontakter"
        size="5xl"
      >
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-6">
                <button
                  type="button"
                  onClick={() => setActiveTab("employees")}
                  className={`border-b-2 px-1 py-3 text-sm font-medium ${tabStyles(
                    activeTab === "employees"
                  )}`}
                >
                  Ansatte
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("customer_contacts")}
                  className={`border-b-2 px-1 py-3 text-sm font-medium ${tabStyles(
                    activeTab === "customer_contacts"
                  )}`}
                >
                  Kundekontakter
                </button>
              </nav>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-full bg-cyan-50 px-3 py-1 text-sm font-medium text-cyan-700">
                Valgte: {selectedCount}
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Sok etter navn, e-post eller telefon"
                className="w-72 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
              />
            </div>
          </div>

          {renderDialogList()}

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={closeDialog}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Avbryt
            </button>
            <button
              type="button"
              disabled={saving || selectedCount === 0}
              onClick={handleAddSelected}
              className="inline-flex items-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Lagrer..." : "Legg til valgte"}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={!!editingContact}
        onClose={closeEditDialog}
        title="Rediger prosjektkontakt"
        size="lg"
      >
        <div className="space-y-5">
          <div>
            <p className="text-sm font-medium text-gray-500">Kontakt</p>
            <p className="mt-1 text-sm font-semibold text-gray-900">
              {getContactMeta(editingContact || {}).person?.displayTitle ||
                getContactMeta(editingContact || {}).person?.name ||
                editingContact?.displayTitle ||
                "-"}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Type
            </label>
            <select
              value={editForm.contact_type}
              onChange={(event) =>
                setEditForm((prev) => ({
                  ...prev,
                  contact_type: event.target.value,
                }))
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            >
              {buildContactTypeOptions(editForm.contact_type).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!editForm.is_primary}
              onChange={(event) =>
                setEditForm((prev) => ({
                  ...prev,
                  is_primary: event.target.checked,
                }))
              }
              className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
            />
            Hovedkontakt
          </label>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={closeEditDialog}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Avbryt
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleEditSave}
              className="inline-flex items-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Lagrer..." : "Lagre endringer"}
            </button>
          </div>
        </div>
      </PMDialog>
    </>
  );
};
